import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Sparkles, Scissors, Truck, ShieldCheck, Star, Package, Award, Heart, Gem, Palette, Users, Zap, Crown, ThumbsUp, Leaf, Flame, Send } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import ProductCarousel from "@/components/ProductCarousel";
import TestimonialsMarquee from "@/components/TestimonialsMarquee";
import { useI18n } from "@/i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const TG_BOT_USERNAME = "aurora_kemitraan_alert_bot";

const WORKSHOP_1_DEFAULT =
  "https://customer-assets.emergentagent.com/job_f62ee21f-a891-493d-9554-4a4c463928c0/artifacts/le3q9f7d_WhatsApp%20Image%202026-06-30%20at%2011.26.07.jpeg";
const WORKSHOP_2_DEFAULT =
  "https://customer-assets.emergentagent.com/job_f62ee21f-a891-493d-9554-4a4c463928c0/artifacts/y3ef10p8_WhatsApp%20Image%202026-06-30%20at%2011.26.08.jpeg";

const ICON_MAP = {
  Scissors, ShieldCheck, Truck, Star, Package, Sparkles, Award, Heart,
  Gem, Palette, Users, Zap, Crown, ThumbsUp, Leaf, Flame, Check,
};

const TIER_DEFAULTS = [
  { name: "Reseller", minOrder: "3 pcs" },
  { name: "Agen", minOrder: "12 pcs", featured: true },
  { name: "Distributor", minOrder: "60 pcs" },
];

const DEFAULT_SIZE_CHART = [
  { size: "S", ld: 92, pb: 136 },
  { size: "M", ld: 96, pb: 140 },
  { size: "L", ld: 102, pb: 140 },
  { size: "XL", ld: 110, pb: 142 },
  { size: "XXL", ld: 120, pb: 143 },
];

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({});
  const { locale, t } = useI18n();
  const [form, setForm] = useState({
    full_name: "",
    whatsapp: "",
    city: "",
    address: "",
    tier: "Reseller",
    instagram: "",
    telegram_username: "",
    date_of_birth: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

  useEffect(() => {
    api
      .get("/products")
      .then((res) => setProducts(res.data))
      .catch(() => toast.error("Gagal memuat produk"))
      .finally(() => setLoading(false));
    api.get("/content/homepage").then((res) => setContent(res.data)).catch(() => {});
  }, []);

  const c = (key, fallback = "") => content[key] || fallback;
  // CMS value wins in Indonesian; in English prefer translation, fall back to CMS/default.
  const L = (cmsKey, i18nKey, fallback = "") =>
    locale === "en" ? t(i18nKey, content[cmsKey] || fallback) : (content[cmsKey] || fallback);

  const tiers = TIER_DEFAULTS.map((d) => {
    const k = d.name.toLowerCase();
    const benefitsRaw =
      c(`tier_${k}_benefits`) || t(`tiers.${k}_benefits`, "");
    return {
      key: k,
      name: c(`tier_${k}_title`, d.name),
      minOrder: c(`tier_${k}_minorder`, d.minOrder),
      discount: L(`tier_${k}_pricelabel`, `tiers.${k}_pricelabel`, `Harga ${d.name}`),
      benefits: benefitsRaw.split("|").map((s) => s.trim()).filter(Boolean),
      cta: c(`tier_${k}_cta`) || `${t("tiers.register_as", "Daftar sebagai")} ${c(`tier_${k}_title`, d.name)}`,
      featured: !!d.featured,
    };
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.whatsapp || !form.city || !form.address) {
      toast.error(t("form.toast_required", "Mohon isi Nama, WhatsApp, Kota, dan Alamat Lengkap"));
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error(t("form.toast_turnstile", "Mohon selesaikan verifikasi anti-spam"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/partnership", { ...form, turnstile_token: turnstileToken });
      toast.success(t("form.toast_success", "Pendaftaran terkirim! Tim Aurora akan menghubungi Anda via WhatsApp."));
      setForm({
        full_name: "",
        whatsapp: "",
        city: "",
        address: "",
        tier: "Reseller",
        instagram: "",
        telegram_username: "",
        date_of_birth: "",
        message: "",
      });
      setTurnstileToken("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-[#FDFBF7] text-[#1C1917]" data-testid="home-page">
      {/* HERO */}
      <section className="relative pt-28 md:pt-40 pb-16 md:pb-28 overflow-hidden">
        <div className="absolute -top-20 -left-20 w-[280px] md:w-[440px] h-[280px] md:h-[440px] rounded-full bg-[#F39237]/15 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-12 items-center relative">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="md:col-span-6"
          >
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <span className="overline text-[#F39237]">{L("hero_overline", "hero.overline", "Produsen Home Dress")}</span>
              <span className="w-12 h-px bg-stone-300" />
              <span className="overline text-stone-500">{L("hero_since", "hero.since", "Sejak 2020")}</span>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-[#0A0A0A] break-words">
              {L("hero_title_a", "hero.title_a", "Home dress yang")}{" "}
              <em className="font-display italic text-[#F39237]">{L("hero_title_em1", "hero.title_em1", "simple")}</em>
              <br />
              {L("hero_title_b", "hero.title_b", "dan")} <em className="font-display italic">{L("hero_title_em2", "hero.title_em2", "adorable")}</em>.
            </h1>

            <p className="mt-5 md:mt-7 text-stone-600 text-base md:text-lg leading-relaxed max-w-xl break-words">
              {L("hero_desc", "hero.desc", "Aurora Hijab adalah rumah produksi daily gamis dari Semarang. Kami berdedikasi pada kenyamanan kain, jahitan rapi, dan skema kemitraan yang menguntungkan untuk reseller, agen, dan distributor di seluruh Indonesia.")}
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#daftar"
                data-testid="hero-cta-daftar"
                className="inline-flex items-center gap-3 px-7 py-4 bg-[#0A0A0A] text-white hover:bg-[#F39237] hover:text-[#0A0A0A] hover:-translate-y-1 transition-all shadow-md hover:shadow-xl group"
              >
                {L("hero_cta_primary", "hero.cta_primary", "Daftar Kemitraan")}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#katalog"
                data-testid="hero-cta-katalog"
                className="inline-flex items-center gap-3 px-7 py-4 border border-stone-300 text-[#0A0A0A] hover:border-[#F39237] hover:text-[#F39237] hover:-translate-y-1 transition-all"
              >
                {L("hero_cta_secondary", "hero.cta_secondary", "Lihat Katalog")}
              </a>
            </div>

            <div className="mt-8 md:mt-12 flex flex-wrap items-center gap-6 sm:gap-8 md:gap-10">
              <div>
                <div className="font-display text-2xl sm:text-3xl text-[#0A0A0A]">{c("stat_partners", "500+")}</div>
                <div className="overline text-stone-500 mt-1">{t("hero.stat_partners_label", "Mitra Aktif")}</div>
              </div>
              <div className="hidden sm:block w-px h-12 bg-stone-300" />
              <div>
                <div className="font-display text-2xl sm:text-3xl text-[#0A0A0A]">{c("stat_pcs", "15k+")}</div>
                <div className="overline text-stone-500 mt-1">{t("hero.stat_pcs_label", "Pcs / Bulan")}</div>
              </div>
              <div className="hidden sm:block w-px h-12 bg-stone-300" />
              <div>
                <div className="font-display text-2xl sm:text-3xl text-[#0A0A0A]">{c("stat_provinces", "34")}</div>
                <div className="overline text-stone-500 mt-1">{t("hero.stat_provinces_label", "Provinsi")}</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="md:col-span-6 relative"
          >
            <div className="relative aspect-[4/5] bg-[#0A0A0A] overflow-hidden">
              <img
                src={c("hero_image_url", "https://customer-assets.emergentagent.com/job_hijab-distributor/artifacts/eydygt5p_WhatsApp%20Image%202026-06-30%20at%2015.36.16.jpeg")}
                alt="Aurora gamis hero"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-6 left-6 bg-[#F39237] text-[#0A0A0A] px-4 py-2 text-xs tracking-widest font-semibold">
                NEW DROP
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE — short rotating tagline strip (kept unique, no repetition) */}
      <section className="bg-[#0A0A0A] text-[#F39237] py-5 md:py-7 overflow-hidden border-y border-stone-800">
        <div className="flex whitespace-nowrap animate-marquee">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-6 md:gap-12 px-4 md:px-6">
              {[
                t("marquee.simple", "Simple & Adorable"),
                t("marquee.semarang", "Made in Semarang"),
                t("marquee.ready", "Ready Stock"),
                t("marquee.dropship", "Free Dropship Label"),
                t("marquee.nation", "Kirim Se-Indonesia"),
              ].map((txt, idx) => (
                <React.Fragment key={idx}>
                  <span className="font-display italic text-xl sm:text-2xl md:text-4xl">
                    {txt}
                  </span>
                  <Sparkles size={18} className="text-[#F39237]/60" />
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* CATALOG */}
      <section id="katalog" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 md:mb-16 gap-6">
            <div>
              <div className="overline text-[#F39237] mb-3 md:mb-4">
                {L("catalog_overline", "catalog.overline", "Koleksi Aurora")}
              </div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight text-[#0A0A0A] max-w-2xl break-words">
                {L("catalog_title", "catalog.title", "Setiap helai dijahit dengan kesabaran dan cinta.")}
              </h2>
            </div>
            <p className="text-stone-600 max-w-md text-sm md:text-base break-words">
              {L("catalog_subtitle", "catalog.subtitle", "Pilih dari koleksi gamis & home dress terbaru kami. Semua siap stock dan siap dikirim ke seluruh Indonesia.")}
            </p>
          </div>

          {loading ? (
            <div data-testid="catalog-loading" className="text-stone-500">{t("catalog.loading", "Memuat produk…")}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12" data-testid="product-grid">
              {products.map((p, i) => (
                <motion.article
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                  className="group transition-transform hover:-translate-y-1"
                  data-testid={`product-card-${p.slug}`}
                >
                  <div className="mb-5">
                    <ProductCarousel
                      images={p.images && p.images.length ? p.images : [p.image_url]}
                      colors={p.colors || []}
                      sizeChartUrl={p.size_chart_url || c("size_chart_default", "")}
                      sizeChartData={p.size_chart_data || DEFAULT_SIZE_CHART}
                      alt={p.name}
                      featured={p.featured}
                    />
                  </div>
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="overline text-stone-500 mb-1 break-words">
                        {p.tagline || "Aurora Collection"}
                      </div>
                      <h3 className="font-display text-2xl text-[#0A0A0A] break-words">
                        {p.name}
                      </h3>
                    </div>
                    <div className="text-right" data-testid={`product-stock-${p.slug}`}>
                      <div className="overline text-stone-400 flex items-center justify-end gap-1">
                        <Package size={11} />
                        {t("catalog.stock_label", "Stok")}
                      </div>
                      <div className={`font-display text-lg ${p.stock > 0 ? "text-[#F39237]" : "text-stone-400"}`}>
                        {p.stock > 0 ? `${p.stock} pcs` : t("catalog.sold_out", "Habis")}
                      </div>
                    </div>
                  </div>
                  {(p.colors || []).length === 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <span className="text-xs text-stone-400 italic">{t("catalog.colors_note", "Warna disesuaikan stok")}</span>
                    </div>
                  )}
                  <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
                    <a
                      href={`https://wa.me/${c("contact_whatsapp", "6287792682795")}?text=${encodeURIComponent(
                        c("contact_whatsapp_text", "Halo Aurora, saya tertarik dengan {product}. Mohon info stok & pengiriman ya.").replace("{product}", p.name)
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`product-cta-${p.slug}`}
                      className="inline-flex items-center gap-2 text-sm text-[#0A0A0A] border-b border-[#0A0A0A] pb-1 hover:text-[#F39237] hover:border-[#F39237] hover:-translate-y-0.5 transition-all"
                    >
                      {t("catalog.ask_wa", "Tanya via WhatsApp")}
                      <ArrowRight size={14} />
                    </a>
                    <a
                      href={`https://t.me/${TG_BOT_USERNAME}?start=produk_${p.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`product-tg-cta-${p.slug}`}
                      className="inline-flex items-center gap-2 text-sm text-[#2AABEE] border-b border-[#2AABEE] pb-1 hover:text-[#F39237] hover:border-[#F39237] hover:-translate-y-0.5 transition-all"
                    >
                      <Send size={13} />
                      {t("catalog.ask_tg", "Tanya via Telegram AI")}
                    </a>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 md:py-32 bg-[#F5F2EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-center">
            <div className="md:col-span-5">
              <div className="overline text-[#F39237] mb-4">{L("why_overline", "why.overline", "Mengapa Aurora")}</div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight text-[#0A0A0A] mb-5 md:mb-6 break-words">
                {L("why_title", "why.title", "Standar yang sama untuk setiap pesanan.")}
              </h2>
              <p className="text-stone-600 leading-relaxed break-words">
                {L("why_desc", "why.desc", "Dari pemilihan kain, pola, hingga proses jahit di rumah produksi sendiri di Semarang. Kami percaya kualitas dimulai dari hal kecil yang dirawat setiap hari.")}
              </p>
            </div>

            <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map((n) => {
                const iconName = c(`why_card_${n}_icon`, ["Scissors", "ShieldCheck", "Truck", "Star"][n - 1]);
                const IconComp = ICON_MAP[iconName] || Sparkles;
                const title = c(
                  `why_card_${n}_title`,
                  ["Produksi Sendiri", "Kain Premium", "Siap Kirim", "Margin Terbaik"][n - 1]
                );
                const desc = c(
                  `why_card_${n}_desc`,
                  [
                    "Rumah produksi sendiri dengan penjahit terlatih untuk konsistensi jahitan & kualitas.",
                    "Rayon crinkle dan twill premium yang adem, jatuh, dan ramah kulit untuk dipakai harian.",
                    "Sistem ready stock & pre-order yang terjadwal. Pengiriman ke seluruh Indonesia.",
                    "Skema harga bertingkat yang adil untuk reseller, agen, hingga distributor.",
                  ][n - 1]
                );
                return (
                  <div
                    key={n}
                    data-testid={`why-card-${n - 1}`}
                    className="bg-[#FDFBF7] border border-stone-200 p-7 hover:border-[#F39237] transition-colors"
                  >
                    <IconComp size={26} className="text-[#F39237] mb-4" />
                    <h3 className="font-display text-xl mb-2 break-words">{title}</h3>
                    <p className="text-sm text-stone-600 leading-relaxed break-words">{desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* WORKSHOP */}
      <section id="workshop" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 md:gap-10 mb-12 md:mb-16 items-end">
            <div className="md:col-span-7">
              <div className="overline text-[#F39237] mb-4">{t("workshop.overline", "Workshop Kami")}</div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight text-[#0A0A0A] break-words">
                {t("workshop.title_a", "Dari rumah produksi di")}{" "}
                <em className="italic text-[#F39237]">{t("workshop.title_em", "Semarang")}</em>
                {t("workshop.title_b", ", langsung ke pintu Anda.")}
              </h2>
            </div>
            <p className="md:col-span-5 text-stone-600 leading-relaxed break-words">
              {t("workshop.desc", "Setiap potongan ditangani penjahit kami yang sudah berpengalaman. Aurora tidak menggunakan jasa makloon massal — kami menjaga proses tetap dekat agar setiap detail tetap rapi.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-7 aspect-[4/3] overflow-hidden">
              <img
                src={c("workshop_image_1", WORKSHOP_1_DEFAULT)}
                alt="Workshop Aurora"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="md:col-span-5 grid grid-rows-2 gap-6">
              <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
                <img
                  src={c("workshop_image_2", WORKSHOP_2_DEFAULT)}
                  alt="Penjahit Aurora"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-[#0A0A0A] text-stone-100 p-8 flex flex-col justify-between">
                <div>
                  <div className="overline text-[#F39237] mb-3">{t("workshop.quote_label", "Quote")}</div>
                  <p className="font-display italic text-2xl leading-snug break-words">
                    {t("workshop.quote", '"Kualitas itu bukan kebetulan, ia adalah kebiasaan kecil yang dijaga setiap hari."')}
                  </p>
                </div>
                <div className="overline text-stone-400 mt-6">
                  {t("workshop.quote_by", "— Founder Aurora Hijab")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TIER PROGRAM — fully CMS controlled */}
      <section id="tier" className="py-20 md:py-32 bg-[#0A0A0A] text-stone-100 relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <div className="overline text-[#F39237] mb-4">{L("tier_overline", "tiers.overline", "Program Kemitraan")}</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight text-white break-words">
              {c("tier_title") ? (
                c("tier_title")
              ) : (
                <>
                  {t("tiers.title_a", "Tiga jalur untuk")}{" "}
                  <em className="italic text-[#F39237]">{t("tiers.title_em", "tumbuh bersama")}</em>{" "}
                  {t("tiers.title_b", "Aurora.")}
                </>
              )}
            </h2>
            <p className="mt-5 text-stone-400 break-words">
              {L("tier_subtitle", "tiers.subtitle", "Pilih level kemitraan yang paling sesuai dengan kebutuhan bisnis Anda. Semakin besar komitmen, semakin baik harganya.")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {tiers.map((tr, i) => (
              <motion.div
                key={tr.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`tier-card-${tr.key}`}
                className={`relative p-7 md:p-10 border ${
                  tr.featured
                    ? "bg-[#F39237] text-[#0A0A0A] border-[#F39237]"
                    : "bg-[#141414] border-stone-800 hover:border-[#F39237] transition-colors"
                }`}
              >
                {tr.featured && (
                  <div className="absolute -top-3 left-8 bg-[#0A0A0A] text-[#F39237] px-3 py-1 text-[10px] tracking-widest font-semibold">
                    {t("tiers.popular", "PALING POPULER")}
                  </div>
                )}
                <div className="overline mb-3 opacity-80">{tr.discount}</div>
                <h3 className="font-display text-3xl mb-2 break-words">{tr.name}</h3>
                <div className="text-sm opacity-80 mb-8">
                  {t("tiers.min_order", "Minimum order")}: <strong>{tr.minOrder}</strong>
                </div>
                <ul className="space-y-3 mb-10">
                  {tr.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm">
                      <Check
                        size={16}
                        className={`flex-shrink-0 mt-0.5 ${tr.featured ? "text-[#0A0A0A]" : "text-[#F39237]"}`}
                      />
                      <span className="break-words">{b}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#daftar"
                  data-testid={`tier-cta-${tr.key}`}
                  className={`inline-flex items-center gap-2 text-sm border-b pb-1 transition-colors ${
                    tr.featured
                      ? "border-[#0A0A0A] text-[#0A0A0A] hover:opacity-80"
                      : "border-stone-100 text-stone-100 hover:text-[#F39237] hover:border-[#F39237]"
                  }`}
                >
                  {tr.cta}
                  <ArrowRight size={14} />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — MEDVi-style infinite marquee */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-10 md:mb-14">
          <div className="overline text-[#F39237] mb-4">{t("testimonials.overline", "Cerita Mitra")}</div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight text-[#0A0A0A] max-w-3xl break-words">
            {t("testimonials.title_a", "Tumbuh bareng mitra dari")}{" "}
            <em className="italic text-[#F39237]">{t("testimonials.title_em", "Sabang sampai Merauke")}</em>.
          </h2>
        </div>
        <TestimonialsMarquee />
      </section>

      {/* REGISTRATION FORM */}
      <section id="daftar" className="py-20 md:py-32 bg-[#F5F2EB] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[320px] md:w-[480px] h-[320px] md:h-[480px] rounded-full bg-[#F39237]/15 blur-3xl pointer-events-none" />
        <div className="max-w-3xl mx-auto px-6 md:px-12 relative">
          <div className="text-center mb-10 md:mb-12">
            <div className="overline text-[#F39237] mb-4">{L("form_overline", "form.overline", "Form Pendaftaran")}</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight text-[#0A0A0A] break-words">
              {c("form_title") ? (
                c("form_title")
              ) : (
                <>
                  {t("form.title_a", "Mulai jadi mitra")} <em className="italic">{t("form.title_em", "Aurora")}</em>{" "}
                  {t("form.title_b", "hari ini.")}
                </>
              )}
            </h2>
            <p className="mt-4 text-stone-600 text-sm md:text-base break-words">
              {L("form_subtitle", "form.subtitle", "Isi form di bawah, tim kami akan menghubungi Anda via WhatsApp dalam 1×24 jam.")}
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="reseller-form"
            className="bg-white border border-stone-200 p-6 md:p-12 space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <FormField
                label={t("form.name", "Nama Lengkap")}
                testId="form-name"
                value={form.full_name}
                onChange={(v) => setForm({ ...form, full_name: v })}
                placeholder={t("form.name_ph", "Nama lengkap Anda")}
                required
              />
              <FormField
                label={t("form.whatsapp", "No. WhatsApp")}
                testId="form-whatsapp"
                value={form.whatsapp}
                onChange={(v) => setForm({ ...form, whatsapp: v })}
                placeholder="08xxxxxxxxxx"
                required
              />
              <FormField
                label={t("form.city", "Kota / Domisili")}
                testId="form-city"
                value={form.city}
                onChange={(v) => setForm({ ...form, city: v })}
                placeholder={t("form.city_ph", "Kota Anda")}
                required
              />
              <div className="md:col-span-2">
                <label className="block overline text-stone-500 mb-2">{t("form.address", "Alamat Lengkap")}</label>
                <textarea
                  data-testid="form-address"
                  required
                  rows={3}
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder={t("form.address_ph", "Nama jalan, No. rumah, RT/RW, Kelurahan, Kecamatan, Kode Pos")}
                  className="w-full px-4 py-3 bg-transparent border border-stone-300 focus:border-[#F39237] focus:ring-2 focus:ring-[#F39237]/20 outline-none resize-none"
                />
              </div>
              <div>
                <label className="block overline text-stone-500 mb-2">
                  {t("form.tier", "Tier yang Diminati")}
                </label>
                <Select
                  value={form.tier}
                  onValueChange={(v) => setForm({ ...form, tier: v })}
                >
                  <SelectTrigger
                    data-testid="form-tier"
                    className="h-12 bg-transparent border-stone-300 rounded-none focus:ring-[#F39237]"
                  >
                    <SelectValue placeholder={t("form.tier_ph", "Pilih tier")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Reseller" data-testid="tier-option-reseller">Reseller</SelectItem>
                    <SelectItem value="Agen" data-testid="tier-option-agen">Agen</SelectItem>
                    <SelectItem value="Distributor" data-testid="tier-option-distributor">Distributor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <FormField
                label={t("form.instagram", "Instagram (opsional)")}
                testId="form-instagram"
                value={form.instagram}
                onChange={(v) => setForm({ ...form, instagram: v })}
                placeholder="@username"
              />
              <FormField
                label={t("form.telegram", "Telegram (opsional)")}
                testId="form-telegram"
                value={form.telegram_username}
                onChange={(v) => setForm({ ...form, telegram_username: v })}
                placeholder="@username"
              />
              <div>
                <label className="block overline text-stone-500 mb-2">
                  {t("form.dob", "Tanggal Lahir")} <span className="text-[#F39237]">*</span>
                </label>
                <input
                  type="date"
                  data-testid="form-dob"
                  required
                  value={form.date_of_birth}
                  onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                  max={new Date(Date.now() - 15 * 365 * 24 * 3600 * 1000).toISOString().split("T")[0]}
                  min="1935-01-01"
                  className="w-full h-12 px-4 bg-transparent border border-stone-300 focus:border-[#F39237] focus:ring-2 focus:ring-[#F39237]/20 outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block overline text-stone-500 mb-2">
                {t("form.message", "Pesan (opsional)")}
              </label>
              <textarea
                data-testid="form-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                placeholder={t("form.message_ph", "Ceritakan rencana bisnis Anda atau pertanyaan khusus…")}
                className="w-full px-4 py-3 bg-transparent border border-stone-300 focus:border-[#F39237] focus:ring-2 focus:ring-[#F39237]/20 outline-none resize-none"
              />
            </div>

            {TURNSTILE_SITE_KEY && (
              <div data-testid="turnstile-wrapper">
                <Turnstile
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={setTurnstileToken}
                  onExpire={() => setTurnstileToken("")}
                  options={{ theme: "light", size: "flexible" }}
                />
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="form-submit"
              className="w-full md:w-auto inline-flex items-center justify-center gap-3 px-10 py-4 bg-[#0A0A0A] text-white hover:bg-[#F39237] hover:text-[#0A0A0A] hover:-translate-y-1 transition-all shadow-md hover:shadow-xl disabled:opacity-60 disabled:hover:transform-none"
            >
              {submitting ? t("form.submitting", "Mengirim…") : t("form.submit", "Kirim Pendaftaran")}
              <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

function FormField({ label, value, onChange, placeholder, required, testId }) {
  return (
    <div>
      <label className="block overline text-stone-500 mb-2">{label}</label>
      <input
        type="text"
        data-testid={testId}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-12 px-4 bg-transparent border border-stone-300 focus:border-[#F39237] focus:ring-2 focus:ring-[#F39237]/20 outline-none"
      />
    </div>
  );
}
